import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtils} from '../shared/http.utils';
import {SynopticFilters} from '../model/filters/synoptic-filters';
import {SynopticsResponse} from '../model/responses/synoptics-response';
import {SynopticPersistRequest} from '../model/requests/synoptic-persist-request';
import {SynopticConfiguration, SynopticConfigurationComponent} from '../model/synoptics/synoptic-configuration-types';
import {SynopticItemUuids} from '../model/synoptics/synoptic-arg-types';
import {SynopticStatesResponse} from '../model/responses/synoptic-states-response';
import {SynopticStatesRequest} from '../model/requests/synoptic-states-request';
import {EncodingUtils} from '../shared/encoding-utils';
import {SynopticFile} from '../model/synoptics/synoptic-file';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {ThumbnailUtils} from '../shared/thumbnail-utils';
import {SvgDecoded, SvgMetaData} from '../model/svg-decoded';
import {NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {SensorIdentifier} from '../model/sensor-identifier';

@Injectable({
  providedIn: 'root'
})
export class SynopticsService {

  private readonly synopticsCache: Map<string, SvgDecoded>;
  private readonly svgPngMap: Map<string, SafeUrl>;
  private readonly synopticsUrl: string;

  constructor(private http: HttpClient, private sanitizer: DomSanitizer, private router: Router) {
    this.synopticsUrl = '/catalog/synoptics';
    this.svgPngMap = new Map<string, SafeUrl>();
    this.synopticsCache = new Map<string, SvgDecoded>();

    this.router.events.pipe(
      filter((event: any): boolean => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart): void => {
      if (!event.url.includes('/synoptics') && (this.svgPngMap.size > 0 || this.synopticsCache.size > 0)) {
        this.svgPngMap.clear();
        this.synopticsCache.clear();
      }
    });
  }

  public getSynoptic(synopticUuid: string): Observable<any> {
    const url: string = `${this.synopticsUrl}/${synopticUuid}`;
    return this.http.get<any>(url);
  }

  public getSynoptics(offset?: number, limit?: number, filters?: SynopticFilters): Observable<SynopticsResponse> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const body: SynopticFilters = filters ?? new SynopticFilters();
    return this.http.post<SynopticsResponse>(this.synopticsUrl, body, options);
  }

  public createSynoptic(synopticCreationData: SynopticPersistRequest): Observable<any> {
    const url: string = `${this.synopticsUrl}/create`;
    return this.http.post<any>(url, synopticCreationData);
  }

  public editSynoptic(synopticEditionData: SynopticPersistRequest): Observable<any> {
    const url: string = `${this.synopticsUrl}/edit`;
    return this.http.post<any>(url, synopticEditionData);
  }

  public deleteSynoptic(synopticUuid: string): Observable<HttpResponse<any>> {
    const url: string = `${this.synopticsUrl}/${synopticUuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public deleteSynoptics(synopticUuids: string[]): Observable<HttpResponse<any>> {
    const url: string = `${this.synopticsUrl}/delete`;
    return this.http.post<any>(url, synopticUuids, {observe: 'response'});
  }

  public findAllMeasurementUuidsInConfig(configObj: SynopticConfiguration | SynopticConfigurationComponent): SynopticItemUuids {
    const result = { deviceMeasurementUuids: [], assetMeasurementUuids: [], kpiUuids: [], deviceUuids: [] };
    const identifiers: SensorIdentifier[] = configObj.states.map(state => state.sensorIdentifier);
    identifiers.forEach((identifier: SensorIdentifier): void => {
      if (identifier?.entityType === 'deviceMeasurement') {
        result.deviceMeasurementUuids.push(identifier.sensorUuid);
      } else if (identifier?.entityType === 'assetMeasurement') {
        result.assetMeasurementUuids.push(identifier.sensorUuid);
      } else if (identifier?.entityType === 'kpi') {
        result.kpiUuids.push(identifier.entityUuid);
      }
    });

    configObj.events
      .map(ev => ev.action)
      .filter(action => action.type === 'showTooltip').forEach(
       action => action.args.rows.forEach(
         row => {
           if (row.value.entityType === 'deviceMeasurement') {
             result.deviceMeasurementUuids.push(row.value.sensorUuid);
           } else if (row.value.entityType === 'assetMeasurement') {
             result.assetMeasurementUuids.push(row.value.sensorUuid);
           } else if (row.value.entityType === 'kpi') {
             result.kpiUuids.push(row.value.entityUuid);
           }
         }
      )
    );

    result.deviceUuids = configObj.events
      .map(ev => ev.action)
      .filter(action => action.type === 'executeCommand')
      .map(action => action.args?.deviceUuid);

    if ('children' in configObj) {
      configObj.children.forEach((childConfigObj: SynopticConfigurationComponent): void => {
        const childResult: SynopticItemUuids = this.findAllMeasurementUuidsInConfig(childConfigObj);
        result.deviceMeasurementUuids.push(...childResult.deviceMeasurementUuids);
        result.assetMeasurementUuids.push(...childResult.assetMeasurementUuids);
        result.kpiUuids.push(...childResult.kpiUuids);
        result.deviceUuids.push(...childResult.deviceUuids);
      });
    }

    for (const key of Object.keys(result)) {
      result[key] = result[key].filter((value, index, self): boolean => self.indexOf(value) === index);
    }

    return result;
  }

  public validateStatsSynoptic(synopticStates: SynopticStatesRequest, tenantUuid: string, organizationUuid: string):
    Observable<SynopticStatesResponse> {

    const url: string = organizationUuid ? `${this.synopticsUrl}/validate/${tenantUuid}/${organizationUuid}` : `${this.synopticsUrl}/validate/${tenantUuid}`;
    return this.http.post<any>(url, synopticStates);
  }

  public async decodeSynopticFilesAndThumbnails(synopticFiles: SynopticFile[]): Promise<void> {
    const promises: Promise<void>[] = [];
    synopticFiles.forEach((synopticFile: SynopticFile): void => {

      const promise: Promise<void> = new Promise((resolve): void => {
        const hash: string = this.customHashRotate(synopticFile.data);
        this.decodeSVG(synopticFile.data, hash);
        this.createThumbnail(synopticFile.data, hash)
          .finally((): void => {
            resolve();
          });
      });
      promises.push(promise);
    });

    await Promise.all(promises);
  }

  public decodeSVG(svgData: string, hash?: string): SvgDecoded {
    hash = hash ?? this.customHashRotate(svgData);

    if (this.synopticsCache.has(hash)) {
      return this.synopticsCache.get(hash);
    }

    const svgString: string = EncodingUtils.base64DecodeUnicode(svgData);
    const parser: DOMParser = new DOMParser();
    const svgElement: HTMLElement = parser.parseFromString(svgString, 'image/svg+xml').documentElement;

    const metaData: SvgMetaData = this.getMetadata(svgElement);

    this.setAsyncDecodingImages(svgElement);

    const svgDecoded: SvgDecoded = {dataDecoded: svgElement.outerHTML, metaData};

    this.synopticsCache.set(hash, svgDecoded);

    return svgDecoded;
  }

  public createThumbnail(svgEncoded: string, hash?: string): Promise<SafeUrl> {
    return new Promise((resolve, reject): void => {
      hash = hash ?? this.customHashRotate(svgEncoded);

      if (this.svgPngMap.has(hash)) {
        resolve(this.svgPngMap.get(hash)!);
        return;
      }

      const svgDecoded: SvgDecoded = this.decodeSVG(svgEncoded, hash);

      ThumbnailUtils.buildSvgThumbnail(svgDecoded).then((pngUrl: string): void => {
        const sanitizedUrl: SafeUrl = this.sanitizer.bypassSecurityTrustUrl(pngUrl);
        this.svgPngMap.set(hash, sanitizedUrl);
        resolve(sanitizedUrl);
      }).catch((error): void => {
        reject(error);
      });

    });
  }

  private getMetadata(svgElement: HTMLElement): SvgMetaData {
    const viewBox: string = svgElement.getAttribute('viewBox');

    let originalWidth: number = parseFloat(svgElement.getAttribute('width')) || 100;
    let originalHeight: number = parseFloat(svgElement.getAttribute('height')) || 100;

    if (viewBox) {
      const viewBoxParts: string[] = viewBox.split(' ');
      originalWidth = parseFloat(viewBoxParts[2] || '100');
      originalHeight = parseFloat(viewBoxParts[3] || '100');
    }

    return {width: originalWidth, height: originalHeight};
  }

  private setAsyncDecodingImages(svgElement: HTMLElement): void {
    const images: SVGImageElement[] = Array.from(svgElement.querySelectorAll('image'));

    images.forEach((image: SVGImageElement): void => {
      const base64Src: string = image.getAttribute('href') || image.getAttribute('xlink:href');
      if (base64Src?.startsWith('data:image')) {
        image.setAttribute('decoding', 'async');
      }
    });
  }

  private customHashRotate(base64String: string): string {
    let hash: number = 0x811c9dc5;
    for (let i = 0; i < base64String.length; i++) {
      hash ^= base64String.charCodeAt(i);
      hash = (hash << 5) | (hash >>> 27);
    }
    return (hash >>> 0).toString(16);
  }

}
